/**
 * The API client.
 *
 * One `request` function does the work: it sends cookies, parses JSON, and
 * turns every non-2xx into an `ApiError` carrying the server's own message.
 * That last part matters -- the API writes messages meant to be read ("ACC 525
 * still holds 4 items"), and a client that replaces them with "Request failed"
 * throws away the useful half of the response.
 */

import type {
  ActivityEntry,
  AppSettings,
  ArchiveQuery,
  AttachmentRead,
  BillTo,
  CalendarDay,
  CalendarFeed,
  CalendarFeedPayload,
  CalendarTestResult,
  Category,
  ChecklistEntry,
  ConsultingClient,
  ConsultingInvoice,
  DashboardSummary,
  EntriesQuery,
  EntriesResponse,
  Item,
  ItemCreatePayload,
  ItemListResponse,
  ItemQuery,
  ItemUpdatePayload,
  Note,
  RateKind,
  RunningTimer,
  StandaloneNote,
  StandaloneNoteSummary,
  Subcategory,
  Tag,
  TagWithCount,
  User,
  WorkDay,
} from './types'

const BASE = '/api'

/**
 * Called whenever any request comes back 401.
 *
 * The session cookie is httpOnly, so the app cannot see it expire -- the first
 * it knows is a request being refused. Routing to the login page from here
 * means every screen inherits that behaviour instead of each one remembering
 * to handle it.
 */
let onUnauthorized: (() => void) | null = null

export function setUnauthorizedHandler(handler: (() => void) | null): void {
  onUnauthorized = handler
}

export class ApiError extends Error {
  readonly status: number
  /** True when the session is gone; the app routes to login rather than
   *  showing an error the user can do nothing about. */
  readonly isUnauthorized: boolean
  readonly retryAfterSeconds: number | null

  constructor(status: number, message: string, retryAfterSeconds: number | null = null) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.isUnauthorized = status === 401
    this.retryAfterSeconds = retryAfterSeconds
  }
}

/** FastAPI reports validation failures as a list of per-field problems. */
interface ValidationDetail {
  loc: (string | number)[]
  msg: string
}

function readErrorDetail(status: number, body: unknown): string {
  if (typeof body === 'object' && body !== null && 'detail' in body) {
    const detail = (body as { detail: unknown }).detail
    if (typeof detail === 'string') return detail
    if (Array.isArray(detail)) {
      // Drop the "body" prefix from loc; the field name is what identifies it.
      return (detail as ValidationDetail[])
        .map((d) => {
          const field = d.loc.filter((p) => p !== 'body').join('.')
          return field ? `${field}: ${d.msg}` : d.msg
        })
        .join('; ')
    }
  }
  if (status >= 500) return 'Something went wrong on the server.'
  return `Request failed (${status}).`
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  let response: Response
  try {
    response = await fetch(`${BASE}${path}`, {
      // The session is an httpOnly cookie; without this it is never sent.
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        // Only for JSON bodies. FormData must be left alone: the browser
        // sets multipart/form-data with a generated boundary, and overriding
        // it produces a body the server cannot parse.
        ...(typeof init.body === 'string' ? { 'Content-Type': 'application/json' } : {}),
        ...init.headers,
      },
      ...init,
    })
  } catch {
    // fetch only rejects for network-level failures, which for a same-origin
    // request means the server is unreachable.
    throw new ApiError(0, 'Cannot reach the server. Check your connection.')
  }

  if (response.status === 204) return undefined as T

  const text = await response.text()
  let body: unknown = null
  if (text) {
    try {
      body = JSON.parse(text)
    } catch {
      body = null
    }
  }

  if (!response.ok) {
    if (response.status === 401) onUnauthorized?.()
    const retryAfter = response.headers.get('retry-after')
    throw new ApiError(
      response.status,
      readErrorDetail(response.status, body),
      retryAfter ? Number(retryAfter) : null,
    )
  }

  return body as T
}

function query(params: Record<string, unknown>): string {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue
    // Repeated keys, not comma-joined: FastAPI reads `?status=a&status=b`.
    if (Array.isArray(value)) {
      for (const entry of value) search.append(key, String(entry))
    } else {
      search.append(key, String(value))
    }
  }
  const encoded = search.toString()
  return encoded ? `?${encoded}` : ''
}

const json = (body: unknown): RequestInit => ({ body: JSON.stringify(body) })

export const api = {
  auth: {
    me: () => request<User>('/auth/me'),
    login: (username: string, password: string) =>
      request<User>('/auth/login', { method: 'POST', ...json({ username, password }) }),
    logout: () => request<{ message: string }>('/auth/logout', { method: 'POST' }),
    changePassword: (current_password: string, new_password: string) =>
      request<{ message: string }>('/auth/password', {
        method: 'POST',
        ...json({ current_password, new_password }),
      }),
  },

  dashboard: {
    summary: () => request<DashboardSummary>('/dashboard'),
  },

  categories: {
    list: (includeArchived = false) =>
      request<Category[]>(`/categories${query({ include_archived: includeArchived })}`),
    create: (name: string, color?: string | null) =>
      request<Category>('/categories', { method: 'POST', ...json({ name, color }) }),
    update: (id: number, patch: { name?: string; color?: string | null; is_archived?: boolean }) =>
      request<Category>(`/categories/${id}`, { method: 'PATCH', ...json(patch) }),
    remove: (id: number) =>
      request<{ message: string }>(`/categories/${id}`, { method: 'DELETE' }),
    reorder: (ids: number[]) =>
      request<{ message: string }>('/categories/reorder', { method: 'POST', ...json({ ids }) }),
  },

  subcategories: {
    list: (categoryId?: number, includeArchived = false) =>
      request<Subcategory[]>(
        `/subcategories${query({ category_id: categoryId, include_archived: includeArchived })}`,
      ),
    create: (category_id: number, name: string, status_note?: string | null) =>
      request<Subcategory>('/subcategories', {
        method: 'POST',
        ...json({ category_id, name, status_note }),
      }),
    update: (
      id: number,
      patch: {
        name?: string
        status_note?: string | null
        is_archived?: boolean
        category_id?: number
      },
    ) => request<Subcategory>(`/subcategories/${id}`, { method: 'PATCH', ...json(patch) }),
    remove: (id: number) =>
      request<{ message: string }>(`/subcategories/${id}`, { method: 'DELETE' }),
    reorder: (ids: number[]) =>
      request<{ message: string }>('/subcategories/reorder', {
        method: 'POST',
        ...json({ ids }),
      }),
  },

  tags: {
    list: () => request<TagWithCount[]>('/tags'),
    create: (name: string, color?: string | null) =>
      request<Tag>('/tags', { method: 'POST', ...json({ name, color }) }),
    update: (id: number, patch: { name?: string; color?: string | null }) =>
      request<Tag>(`/tags/${id}`, { method: 'PATCH', ...json(patch) }),
    remove: (id: number) => request<{ message: string }>(`/tags/${id}`, { method: 'DELETE' }),
    merge: (source_id: number, target_id: number) =>
      request<{ message: string }>('/tags/merge', {
        method: 'POST',
        ...json({ source_id, target_id }),
      }),
  },

  items: {
    list: (params: ItemQuery = {}) =>
      request<ItemListResponse>(`/items${query(params as Record<string, unknown>)}`),
    get: (id: number) => request<Item>(`/items/${id}`),
    create: (payload: ItemCreatePayload) =>
      request<Item>('/items', { method: 'POST', ...json(payload) }),
    update: (id: number, patch: ItemUpdatePayload) =>
      request<Item>(`/items/${id}`, { method: 'PATCH', ...json(patch) }),
    remove: (id: number) => request<{ message: string }>(`/items/${id}`, { method: 'DELETE' }),
    setManualRank: (ids: number[]) =>
      request<{ message: string }>('/items/rank', { method: 'POST', ...json({ ids }) }),
    clearManualRank: (ids: number[]) =>
      request<{ message: string }>('/items/rank/clear', { method: 'POST', ...json({ ids }) }),
    activity: (id: number) => request<ActivityEntry[]>(`/items/${id}/activity`),
  },

  settings: {
    read: () => request<AppSettings>('/settings'),
    update: (patch: Partial<AppSettings>) =>
      request<AppSettings>('/settings', { method: 'PATCH', ...json(patch) }),
    regenerateIcsToken: () =>
      request<AppSettings>('/settings/ics-token', { method: 'POST' }),
  },

  calendars: {
    list: () => request<CalendarFeed[]>('/calendars'),
    create: (payload: CalendarFeedPayload) =>
      request<CalendarFeed>('/calendars', { method: 'POST', ...json(payload) }),
    update: (id: number, patch: Partial<CalendarFeedPayload>) =>
      request<CalendarFeed>(`/calendars/${id}`, { method: 'PATCH', ...json(patch) }),
    remove: (id: number) =>
      request<{ message: string }>(`/calendars/${id}`, { method: 'DELETE' }),
    refresh: (id: number) =>
      request<CalendarFeed>(`/calendars/${id}/refresh`, { method: 'POST' }),
    /** Force every feed, ignoring the rate limit. Several seconds per feed. */
    refreshAll: () => request<CalendarFeed[]>('/calendars/refresh', { method: 'POST' }),
    test: (payload: CalendarFeedPayload) =>
      request<CalendarTestResult>('/calendars/test', { method: 'POST', ...json(payload) }),
    /** Omit `on` for today. */
    day: (on?: string) => request<CalendarDay>(`/calendars/day${query({ on })}`),
  },

  archive: {
    list: (params: ArchiveQuery = {}) =>
      request<ItemListResponse>(`/archive${query(params as Record<string, unknown>)}`),
    restore: (id: number) =>
      request<Item>(`/archive/${id}/restore`, { method: 'POST' }),
    stats: () => request<{ done: number; dropped: number; total: number }>('/archive/stats'),
  },

  notes: {
    list: (itemId: number) => request<Note[]>(`/notes${query({ item_id: itemId })}`),
    create: (item_id: number, body_html: string) =>
      request<Note>('/notes', { method: 'POST', ...json({ item_id, body_html }) }),
    update: (id: number, body_html: string) =>
      request<Note>(`/notes/${id}`, { method: 'PATCH', ...json({ body_html }) }),
    remove: (id: number) => request<{ message: string }>(`/notes/${id}`, { method: 'DELETE' }),
  },

  standaloneNotes: {
    list: (q?: string) =>
      request<StandaloneNoteSummary[]>(`/standalone-notes${query({ q })}`),
    get: (id: number) => request<StandaloneNote>(`/standalone-notes/${id}`),
    create: (title?: string, body_html = '') =>
      request<StandaloneNote>('/standalone-notes', {
        method: 'POST',
        ...json({ title, body_html }),
      }),
    update: (id: number, patch: { title?: string | null; body_html?: string }) =>
      request<StandaloneNote>(`/standalone-notes/${id}`, {
        method: 'PATCH',
        ...json(patch),
      }),
    remove: (id: number) =>
      request<{ message: string }>(`/standalone-notes/${id}`, { method: 'DELETE' }),
  },

  checklist: {
    list: (itemId: number) =>
      request<ChecklistEntry[]>(`/checklist${query({ item_id: itemId })}`),
    create: (item_id: number, text: string) =>
      request<ChecklistEntry>('/checklist', { method: 'POST', ...json({ item_id, text }) }),
    update: (id: number, patch: { text?: string; is_done?: boolean }) =>
      request<ChecklistEntry>(`/checklist/${id}`, { method: 'PATCH', ...json(patch) }),
    remove: (id: number) =>
      request<{ message: string }>(`/checklist/${id}`, { method: 'DELETE' }),
    reorder: (ids: number[]) =>
      request<{ message: string }>('/checklist/reorder', { method: 'POST', ...json({ ids }) }),
  },

  attachments: {
    list: (itemId: number) =>
      request<AttachmentRead[]>(`/attachments${query({ item_id: itemId })}`),
    /** Multipart, so no JSON Content-Type -- the browser must set the boundary
     *  itself, and setting it by hand produces a body the server cannot parse. */
    upload: (
      file: File,
      owner: { item_id?: number; note_id?: number; standalone_note_id?: number },
    ) => {
      const form = new FormData()
      form.append('file', file)
      if (owner.item_id !== undefined) form.append('item_id', String(owner.item_id))
      if (owner.note_id !== undefined) form.append('note_id', String(owner.note_id))
      if (owner.standalone_note_id !== undefined)
        form.append('standalone_note_id', String(owner.standalone_note_id))
      return request<AttachmentRead>('/attachments', { method: 'POST', body: form })
    },
    remove: (id: number) =>
      request<{ message: string }>(`/attachments/${id}`, { method: 'DELETE' }),
  },

  /**
   * Consulting.
   *
   * Every write returns the whole affected day rather than the thing that was
   * written, because the day is what the screen shows: stopping a timer
   * changes the day's total (rounding moved the end time), and editing a
   * segment can move it onto a different day entirely.
   */
  consulting: {
    clients: {
      list: (includeArchived = false) =>
        request<ConsultingClient[]>(
          `/consulting/clients${query({ include_archived: includeArchived })}`,
        ),
      create: (payload: {
        name: string
        default_rate_kind?: RateKind
        default_rate?: number | null
        default_bill_to_id?: number | null
      }) => request<ConsultingClient>('/consulting/clients', { method: 'POST', ...json(payload) }),
      update: (
        id: number,
        patch: {
          name?: string
          default_rate_kind?: RateKind
          default_rate?: number | null
          default_bill_to_id?: number | null
          is_archived?: boolean
        },
      ) =>
        request<ConsultingClient>(`/consulting/clients/${id}`, {
          method: 'PATCH',
          ...json(patch),
        }),
      remove: (id: number) =>
        request<{ message: string }>(`/consulting/clients/${id}`, { method: 'DELETE' }),
    },

    billTos: {
      list: (includeArchived = false) =>
        request<BillTo[]>(`/consulting/bill-tos${query({ include_archived: includeArchived })}`),
      create: (payload: { name: string; address?: string | null; re_line?: string | null }) =>
        request<BillTo>('/consulting/bill-tos', { method: 'POST', ...json(payload) }),
      update: (
        id: number,
        patch: { name?: string; address?: string | null; re_line?: string | null; is_archived?: boolean },
      ) => request<BillTo>(`/consulting/bill-tos/${id}`, { method: 'PATCH', ...json(patch) }),
      remove: (id: number) =>
        request<{ message: string }>(`/consulting/bill-tos/${id}`, { method: 'DELETE' }),
    },

    entries: (params: EntriesQuery = {}) =>
      request<EntriesResponse>(`/consulting/entries${query(params as Record<string, unknown>)}`),

    /** The CSV export is a download, not JSON — hand the browser the URL. */
    exportUrl: (params: EntriesQuery = {}) =>
      `${BASE}/consulting/export.csv${query(params as Record<string, unknown>)}`,

    timer: {
      read: () => request<RunningTimer>('/consulting/timer'),
      /** `rate` omitted, or its fields absent, means the client's default. */
      start: (
        client_id: number,
        note?: string,
        rate: { rate_kind?: RateKind; rate?: number | null } = {},
      ) =>
        request<RunningTimer>('/consulting/timer/start', {
          method: 'POST',
          ...json({ client_id, note: note || null, ...rate }),
        }),
      stop: (note?: string) =>
        request<WorkDay>('/consulting/timer/stop', {
          method: 'POST',
          ...json({ note: note || null }),
        }),
    },

    segments: {
      /** Times go up as instants (`toISOString()`), so "9:00" is never
       *  ambiguous about which 9:00 it meant. */
      create: (payload: {
        client_id: number
        started_at: string
        ended_at: string
        note?: string | null
        rate_kind?: RateKind
        rate?: number | null
      }) => request<WorkDay>('/consulting/segments', { method: 'POST', ...json(payload) }),
      update: (
        id: number,
        patch: { started_at?: string; ended_at?: string; note?: string | null },
      ) => request<WorkDay>(`/consulting/segments/${id}`, { method: 'PATCH', ...json(patch) }),
      remove: (id: number) =>
        request<{ message: string }>(`/consulting/segments/${id}`, { method: 'DELETE' }),
    },

    days: {
      update: (
        id: number,
        patch: {
          client_id?: number
          work_date?: string
          rate_kind?: RateKind
          rate?: number | null
          activity?: string | null
        },
      ) => request<WorkDay>(`/consulting/days/${id}`, { method: 'PATCH', ...json(patch) }),
      remove: (id: number) =>
        request<{ message: string }>(`/consulting/days/${id}`, { method: 'DELETE' }),
      /** Detaches the row from its invoice. The invoice keeps its line. */
      unarchive: (id: number) =>
        request<WorkDay>(`/consulting/days/${id}/unarchive`, { method: 'POST' }),
    },

    invoices: {
      list: () => request<ConsultingInvoice[]>('/consulting/invoices'),
      get: (id: number) => request<ConsultingInvoice>(`/consulting/invoices/${id}`),
      create: (payload: {
        day_ids: number[]
        bill_to_id: number
        invoice_date?: string
        period_label?: string
        re_line?: string | null
      }) => request<ConsultingInvoice>('/consulting/invoices', { method: 'POST', ...json(payload) }),
      update: (id: number, patch: Partial<ConsultingInvoice>) =>
        request<ConsultingInvoice>(`/consulting/invoices/${id}`, {
          method: 'PATCH',
          ...json(patch),
        }),
      remove: (id: number) =>
        request<{ message: string }>(`/consulting/invoices/${id}`, { method: 'DELETE' }),
      downloadUrl: (id: number) => `${BASE}/consulting/invoices/${id}/docx`,
    },
  },
}
